import { Component } from '@angular/core';
import { ConfirmationMode } from '../shared/share';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '../shared/services/notifications.service';
import { finalize, interval, Subject, Subscription, takeUntil, takeWhile } from 'rxjs';
import { ConfirmationmodalComponent } from '../shared/components/confirmationmodal/confirmationmodal.component';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { LoaderComponent } from "../shared/loader/loader.component";
import { FileUploadComponent } from "../shared/file-upload/file-upload.component";
import { MemberInsurerRestService } from '../../services/member-insurer-rest.service';
import { MemberBulkUploadResponse } from '../../models/commonmodel';
import { MatProgressBarModule } from '@angular/material/progress-bar';
@Component({
  selector: 'app-patient-bulkupload',
  standalone: true,
  imports: [CommonModule, MatIconModule, LoaderComponent, FileUploadComponent, MatProgressBarModule],
  templateUrl: './patient-bulkupload.component.html',
  styleUrl: './patient-bulkupload.component.scss'
})
export class PatientBulkuploadComponent {

  enumConfirmationMode = ConfirmationMode;
  isFileUploading: boolean = false;
  isDownloading: boolean = false;
  private circumference: number = 2 * Math.PI * 20;
  popupTitle = 'Bulk Upload';
  popupSubtitle = 'Upload as many as applicants at once by uploading a CSV file.';
  protected unsubscribe = new Subject();

  downloadState: 'initial' | 'downloading' | 'done' | 'error' = 'initial';
  downloadProgress: number = 0;
  private progressSimulationSubscription: Subscription | null = null;

  constructor(
    public dialogRef: MatDialogRef<PatientBulkuploadComponent>,
    public dialog: MatDialog,
    private notificationService: NotificationService, private restService: MemberInsurerRestService

  ) { }

  ngOnInit(): void {
    this.resetDownloadState();
  }

  closeDialog() {
    this.dialogRef.close();
  }

  get progressStrokeDasharray(): string {
    const offset = this.circumference * (1 - this.downloadProgress / 100);
    return `${offset} ${this.circumference}`;
  }

  resetDownloadState(): void {
    this.downloadState = 'initial';
    this.downloadProgress = 0;
    this.isDownloading = false;

    if (this.progressSimulationSubscription) {
      this.progressSimulationSubscription.unsubscribe();
      this.progressSimulationSubscription = null;
    }
  }

  downloadAddMemberTemplate(): void {
    if (this.isDownloading) {
      return;
    }

    this.isDownloading = true;
    this.downloadState = 'downloading';
    this.downloadProgress = 0;

    this.progressSimulationSubscription = interval(50)
      .pipe(
        takeWhile(() => this.downloadProgress < 95),
        takeUntil(this.unsubscribe)
      )
      .subscribe(() => {
        this.downloadProgress += 1;
        if (this.downloadProgress >= 95) {
          this.downloadProgress = 95;
          if (this.progressSimulationSubscription) {
            this.progressSimulationSubscription.unsubscribe();
            this.progressSimulationSubscription = null;
          }
        }
      });

    // --- NEW: Wait for 2 seconds before calling the API ---
    setTimeout(() => {
      this.restService.downloadMemberTemplate()
        .pipe(
          takeUntil(this.unsubscribe),
          finalize(() => {
            if (this.progressSimulationSubscription) {
              this.progressSimulationSubscription.unsubscribe();
              this.progressSimulationSubscription = null;
            }
          })
        )
        .subscribe({
          next: (file: Blob) => {
            this.downloadProgress = 100;
            this.downloadState = 'done';

            setTimeout(() => {
              const downloadURL = window.URL.createObjectURL(file);
              const link = document.createElement('a');
              link.download = `Add Member Template File.csv`;
              link.href = downloadURL;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(downloadURL);

              this.notificationService.showNotification('File downloaded successfully');

              setTimeout(() => {
                this.resetDownloadState();
              }, 1500);
            }, 300);

          },
          error: (err: any) => {
            this.notificationService.showNotification('File download failed!', 'error');
            this.downloadState = 'error';
            this.downloadProgress = 0;

            setTimeout(() => {
              this.resetDownloadState();
            }, 2000);
          }
        });
    }, 500); // 2-second delay here
  }

  selectedFile: File | null = null;
  uploadProgress = 0;

  confirmation(file: File): void {
    this.selectedFile = file;
  }

  uploadFile(file: File): void {
    this.isFileUploading = true;
    this.uploadProgress = 0;

    // Simulate progress over 3 seconds
    const interval = setInterval(() => {
      if (this.uploadProgress < 100) {
        this.uploadProgress += 2;
      }
    }, 60);

    this.restService.uploadBulkMembers(file)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: MemberBulkUploadResponse) => {
          clearInterval(interval);
          this.uploadProgress = 100;

          if (response.message?.toLowerCase().includes('partially')) {
            this.notificationService.showNotification(
              'File upload processed partially, downloading file with error records',
              'error'
            );
            setTimeout(() => this.downloadErrorRecord(response.errorFileName), 500);
          } else {
            this.notificationService.showNotification('File successfully uploaded');
            this.closeDialog();
          }

          this.resetUploadState();
        },
        error: (error: HttpErrorResponse) => {
          clearInterval(interval);
          this.notificationService.showNotification('Upload failed', 'error');
          this.resetUploadState();
          this.handleError(error);
        }
      });
  }

  resetUploadState(): void {
    this.isFileUploading = false;
    this.selectedFile = null;
    this.uploadProgress = 0;
  }


  downloadErrorRecord(errorFileName: string): void {
    // this.restService.downloadErrorTariff(errorFileName)
    //   .pipe(takeUntil(this.unsubscribe))
    //   .subscribe({
    //     next: (file: Blob) => {
    //       const downloadURL = window.URL.createObjectURL(file);
    //       const link = document.createElement('a');
    //       link.download = `error-records-asset.csv`;
    //       link.href = downloadURL;
    //       link.click();
    //       this.notificationService.showNotification('File uploaded successfully');
    //       this.isFileUploading = false;
    //       this.dialogRef.close(true);
    //     },
    //     error: (error: HttpErrorResponse) => {
    //       this.isFileUploading = false;
    //       this.handleError(error);
    //     },
    //   });
  }

  handleError(error: HttpErrorResponse): void {
    this.notificationService.showNotification(error.error.error, 'error');
    console.log(error);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(false);
    this.unsubscribe.complete();
    if (this.progressSimulationSubscription) {
      this.progressSimulationSubscription.unsubscribe();
    }
  }
}